import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { Link, useNavigate } from "@tanstack/react-router";
import {
  X,
  Search,
  Home,
  Briefcase,
  LayoutGrid,
  Crown,
  Sparkles,
  Store,
  Wallet,
  Megaphone,
  LifeBuoy,
  Settings,
  Phone,
  ShieldCheck,
  Menu,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { cn } from "@/lib/utils";
import { useAuth } from "@/hooks/use-auth";
import { useStaffRole } from "@/components/user-menu";
import { WHATSAPP_LINK } from "@/lib/balance";

export function SideDrawer() {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const navigate = useNavigate();
  const { user } = useAuth();
  const role = useStaffRole(user?.id);
  const isStaff = role === "admin" || role === "moderator";

  useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    window.addEventListener("keydown", onKey);
    return () => {
      document.body.style.overflow = prev;
      window.removeEventListener("keydown", onKey);
    };
  }, [open]);

  const close = () => setOpen(false);
  const [mounted, setMounted] = useState(false);
  useEffect(() => setMounted(true), []);

  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    close();
    navigate({ to: "/elanlar", search: { q, kateqoriya: "", seher: "", sirala: "yeni" } });
  };

  const itemClass =
    "flex items-center gap-3 rounded-xl px-3 py-3 text-sm font-medium transition-colors hover:bg-accent";

  return (
    <>
      <Button
        variant="ghost"
        size="sm"
        aria-label="Kataloq"
        aria-expanded={open}
        className="size-10 shrink-0 rounded-full px-0 md:h-10 md:w-auto md:px-4"
        onClick={() => setOpen(true)}
      >
        <Menu className="size-6 md:size-4" /> <span className="hidden md:inline">Kataloq</span>
      </Button>

      {mounted &&
        createPortal(
          <>
            {/* Overlay */}
            <div
        onClick={close}
        aria-hidden={!open}
        className={cn(
          "fixed inset-0 z-[60] bg-black/60 backdrop-blur-[2px] transition-opacity duration-300",
          open ? "opacity-100" : "pointer-events-none opacity-0",
        )}
      />

      {/* Panel */}
      <div
        role="dialog"
        aria-modal="true"
        aria-label="Menyu"
        className={cn(
          "fixed inset-y-0 right-0 z-[70] flex w-[86%] max-w-sm flex-col border-l border-border bg-background shadow-elevated transition-transform duration-300 ease-out",
          open ? "translate-x-0" : "translate-x-full",
        )}
      >
        <div className="flex items-center justify-between border-b border-border px-4 py-4">
          <span className="text-base font-extrabold tracking-tight">Kataloq</span>
          <Button variant="ghost" size="icon" className="rounded-full" aria-label="Bağla" onClick={close}>
            <X className="size-5" />
          </Button>
        </div>

        <div className="flex-1 overflow-y-auto px-4 py-4">
          <form onSubmit={submit} className="relative">
            <Search className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" />
            <Input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Axtar..."
              aria-label="Axtar"
              className="h-11 rounded-full pl-9"
            />
          </form>

          <div className="mt-3 grid grid-cols-2 gap-2">
            <Link to="/balans" onClick={close}>
              <Button variant="outline" className="h-10 w-full rounded-full text-sm font-semibold">
                Balans göndər
              </Button>
            </Link>
            <a href={WHATSAPP_LINK} target="_blank" rel="noreferrer" onClick={close}>
              <Button className="h-10 w-full rounded-full bg-destructive text-destructive-foreground text-sm font-semibold hover:bg-destructive/90">
                Balans artır
              </Button>
            </a>
          </div>

          <nav className="mt-4 flex flex-col gap-1">
            <Link to="/" onClick={close} className={itemClass}>
              <Home className="size-4 text-muted-foreground" /> Ana səhifə
            </Link>
            <Link
              to="/elanlar"
              search={{ q: "", kateqoriya: "", seher: "", sirala: "yeni" }}
              onClick={close}
              className={itemClass}
            >
              <LayoutGrid className="size-4 text-muted-foreground" /> Kateqoriyalar / Bütün elanlar
            </Link>
            <Link to="/is-elanlari" onClick={close} className={itemClass}>
              <Briefcase className="size-4 text-muted-foreground" /> İş elanları
            </Link>
            <Link to="/reklam" onClick={close} className={itemClass}>
              <Megaphone className="size-4 text-muted-foreground" /> Reklam yerləşdir
            </Link>
            <Link
              to="/elanlar"
              search={{ q: "", kateqoriya: "", seher: "", sirala: "vip" }}
              onClick={close}
              className={cn(itemClass, "bg-premium/15 text-foreground hover:bg-premium/25")}
            >
              <Crown className="size-4" /> VIP elanlar
            </Link>
            <Link
              to="/elanlar"
              search={{ q: "", kateqoriya: "", seher: "", sirala: "premium" }}
              onClick={close}
              className={cn(itemClass, "bg-info/15 text-foreground hover:bg-info/25")}
            >
              <Sparkles className="size-4" /> Premium elanlar
            </Link>
            <Link to="/magazalar" onClick={close} className={itemClass}>
              <Store className="size-4 text-muted-foreground" /> Mağazalar
            </Link>
            <Link to="/magazam" onClick={close} className={itemClass}>
              <Store className="size-4 text-muted-foreground" /> Mağazam (idarəetmə)
            </Link>
            <Link to="/balans" onClick={close} className={itemClass}>
              <Wallet className="size-4 text-muted-foreground" /> Balans
            </Link>
            <a href="/#paketler" onClick={close} className={itemClass}>
              <Megaphone className="size-4 text-muted-foreground" /> Kampaniyalar (VIP / Premium)
            </a>
            <Link to="/elaqe" onClick={close} className={itemClass}>
              <LifeBuoy className="size-4 text-muted-foreground" /> Dəstək / Adminə yaz
            </Link>
            <Link to="/profil" onClick={close} className={itemClass}>
              <Settings className="size-4 text-muted-foreground" /> Parametrlər
            </Link>
            <Link to="/elaqe" onClick={close} className={itemClass}>
              <Phone className="size-4 text-muted-foreground" /> Əlaqə
            </Link>
            {isStaff && (
              <Link
                to="/admin"
                onClick={close}
                className={cn(itemClass, "text-primary hover:bg-primary/10")}
              >
                <ShieldCheck className="size-4" /> Admin panel
              </Link>
            )}
          </nav>
        </div>
      </div>
          </>,
          document.body,
        )}
    </>
  );
}